/**
 * BucketBrowser (MUI)
 *
 * 테넌트별 GCS 버킷 브라우징/파일 조회/편집.
 * teams-tab BucketBrowser.tsx 의 MUI 포팅.
 */

import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import FolderIcon from "@mui/icons-material/Folder";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import RefreshIcon from "@mui/icons-material/Refresh";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import DownloadIcon from "@mui/icons-material/Download";

import * as bucketApi from "../api/bucket";
import type {
  BucketFileContent,
  BucketItem,
} from "../api/bucket";
import { getAuthToken } from "../api/base";
import { formatShortDate } from "../helpers/format";

const EDITABLE_EXT = [
  ".json", ".md", ".py", ".txt", ".csv", ".yaml", ".yml",
  ".sh", ".js", ".ts", ".cfg", ".ini", ".xml", ".html",
];

function formatSize(bytes?: number): string {
  if (bytes == null || bytes === 0) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function isEditable(path: string, truncated?: boolean): boolean {
  if (truncated) return false;
  const lower = path.toLowerCase();
  return EDITABLE_EXT.some((ext) => lower.endsWith(ext));
}

interface BucketBrowserProps {
  tenantCode: string;
}

// 에이전트가 실제로 읽/쓰는 두 최상위 폴더 — 바로가기로 고정 노출한다.
const PATH_SHORTCUTS: { label: string; prefix: string; hint: string }[] = [
  {
    label: "AIFYAGENT_INPUT",
    prefix: "AIFYAGENT_INPUT/",
    hint: "변환 스크립트·convert_meta",
  },
  {
    label: "AIFYAGENT_OUTPUT",
    prefix: "AIFYAGENT_OUTPUT/",
    hint: "변환 결과·converter_history·스킬 리포트",
  },
];

export function BucketBrowser({ tenantCode }: BucketBrowserProps) {
  const [prefix, setPrefix] = useState("AIFYAGENT_INPUT/");
  const [bucket, setBucket] = useState("");
  const [directories, setDirectories] = useState<BucketItem[]>([]);
  const [files, setFiles] = useState<BucketItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [viewingPath, setViewingPath] = useState<string | null>(null);
  const [content, setContent] = useState<BucketFileContent | null>(null);
  const [fileLoading, setFileLoading] = useState(false);

  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState("");
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ severity: "success" | "error"; message: string } | null>(null);

  const closeFile = () => {
    setViewingPath(null);
    setContent(null);
    setEditing(false);
    setEditValue("");
    setToast(null);
  };

  const loadDirectory = useCallback(
    async (newPrefix: string) => {
      setLoading(true);
      setError(null);
      closeFile();
      try {
        const data = await bucketApi.browseBucket(tenantCode, newPrefix);
        setDirectories(data.directories);
        setFiles(data.files);
        setBucket(data.bucket);
        setPrefix(newPrefix);
      } catch (e) {
        setError(e instanceof Error ? e.message : "버킷 조회 실패");
      } finally {
        setLoading(false);
      }
    },
    [tenantCode],
  );

  useEffect(() => {
    // 첫 진입은 AIFYAGENT_INPUT/ 바로가기 위치로 시작 (실제 에이전트 사용 폴더).
    if (tenantCode) loadDirectory("AIFYAGENT_INPUT/");
  }, [tenantCode, loadDirectory]);

  const navigateUp = () => {
    if (!prefix) return;
    const parts = prefix.replace(/\/$/, "").split("/");
    parts.pop();
    const parent = parts.length > 0 ? `${parts.join("/")}/` : "";
    loadDirectory(parent);
  };

  const openFile = async (path: string) => {
    setViewingPath(path);
    setFileLoading(true);
    setEditing(false);
    setToast(null);
    try {
      const data = await bucketApi.readBucketFile(tenantCode, path);
      setContent(data);
    } catch (e) {
      setContent({
        path,
        size: 0,
        contentType: null,
        truncated: true,
        content: `[오류] ${e instanceof Error ? e.message : "파일 읽기 실패"}`,
      });
    } finally {
      setFileLoading(false);
    }
  };

  const startEditing = () => {
    if (!content) return;
    setEditValue(content.content);
    setEditing(true);
    setToast(null);
  };

  const saveFile = async () => {
    if (!viewingPath) return;
    setSaving(true);
    setToast(null);
    try {
      const result = await bucketApi.saveBucketFile(tenantCode, viewingPath, editValue);
      setContent((prev) =>
        prev
          ? {
              ...prev,
              content: editValue,
              size: result.size,
              updated: result.updated,
              truncated: false,
            }
          : prev,
      );
      setEditing(false);
      setToast({ severity: "success", message: "저장 완료" });
      setTimeout(() => setToast(null), 3000);
    } catch (e) {
      setToast({
        severity: "error",
        message: e instanceof Error ? `저장 실패: ${e.message}` : "저장 실패",
      });
    } finally {
      setSaving(false);
    }
  };

  const downloadFile = async (path: string) => {
    try {
      const token = await getAuthToken();
      const url = bucketApi.getBucketDownloadUrl(tenantCode, path);
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const href = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = href;
      a.download = path.split("/").pop() || "download";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(href);
    } catch (e) {
      setError(e instanceof Error ? `다운로드 실패: ${e.message}` : "다운로드 실패");
    }
  };

  // breadcrumb 조각
  const crumbs = prefix ? prefix.replace(/\/$/, "").split("/") : [];

  const activeShortcut = PATH_SHORTCUTS.find((s) => prefix.startsWith(s.prefix))?.prefix;

  return (
    <Box sx={{ display: "flex", height: "100%", minHeight: 500, gap: 1 }}>
      {/* 좌측: 디렉터리 + 파일 */}
      <Paper
        variant="outlined"
        sx={{ flex: viewingPath ? "0 0 45%" : 1, display: "flex", flexDirection: "column" }}
      >
        {/* 바로가기: 에이전트가 실제로 사용하는 INPUT / OUTPUT 폴더로 즉시 점프 */}
        <Box
          sx={{
            px: 1,
            py: 0.8,
            borderBottom: 1,
            borderColor: "divider",
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            flexWrap: "wrap",
          }}
        >
          <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
            바로가기
          </Typography>
          {PATH_SHORTCUTS.map((s) => (
            <Tooltip key={s.prefix} title={s.hint}>
              <Chip
                size="small"
                label={s.label}
                variant={activeShortcut === s.prefix ? "filled" : "outlined"}
                color={activeShortcut === s.prefix ? "primary" : "default"}
                onClick={() => loadDirectory(s.prefix)}
              />
            </Tooltip>
          ))}
          <Box sx={{ flex: 1 }} />
          <Chip
            size="small"
            label="버킷 루트"
            variant={prefix === "" ? "filled" : "outlined"}
            onClick={() => loadDirectory("")}
          />
        </Box>

        <Box
          sx={{
            p: 1,
            borderBottom: 1,
            borderColor: "divider",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Tooltip title="상위 폴더">
            <span>
              <IconButton size="small" onClick={navigateUp} disabled={!prefix}>
                <ArrowUpwardIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Breadcrumbs separator="/" sx={{ flex: 1, overflow: "auto" }}>
            <Link
              component="button"
              onClick={() => loadDirectory("")}
              underline="hover"
              sx={{ fontFamily: "monospace", fontSize: 12 }}
            >
              {bucket || "bucket"}
            </Link>
            {crumbs.map((seg, i) => {
              const p = `${crumbs.slice(0, i + 1).join("/")}/`;
              return (
                <Link
                  key={p}
                  component="button"
                  onClick={() => loadDirectory(p)}
                  underline="hover"
                  sx={{ fontFamily: "monospace", fontSize: 12 }}
                >
                  {seg}
                </Link>
              );
            })}
          </Breadcrumbs>
          <Tooltip title="새로고침">
            <IconButton size="small" onClick={() => loadDirectory(prefix)}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        {error && <Alert severity="error" sx={{ m: 1 }}>{error}</Alert>}

        <Box sx={{ flex: 1, overflow: "auto" }}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress size={24} />
            </Box>
          ) : directories.length === 0 && files.length === 0 ? (
            <Box sx={{ p: 3, textAlign: "center" }}>
              <Typography variant="caption" color="text.secondary">
                비어 있습니다
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>이름</TableCell>
                    <TableCell align="right">크기</TableCell>
                    <TableCell>수정</TableCell>
                    <TableCell align="right" sx={{ width: 80 }}></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {directories.map((d) => (
                    <TableRow
                      key={d.path}
                      hover
                      sx={{ cursor: "pointer" }}
                      onClick={() => loadDirectory(d.path)}
                    >
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <FolderIcon fontSize="small" color="action" />
                          <Typography variant="body2">{d.name}</Typography>
                        </Stack>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="caption" color="text.secondary">
                          —
                        </Typography>
                      </TableCell>
                      <TableCell>—</TableCell>
                      <TableCell />
                    </TableRow>
                  ))}
                  {files.map((f) => {
                    const isView = viewingPath === f.path;
                    return (
                      <TableRow
                        key={f.path}
                        hover
                        selected={isView}
                        sx={{ cursor: "pointer" }}
                        onClick={() => openFile(f.path)}
                      >
                        <TableCell>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <InsertDriveFileIcon fontSize="small" color="action" />
                            <Typography variant="body2">{f.name}</Typography>
                          </Stack>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="caption">{formatSize(f.size)}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">{formatShortDate(f.updated)}</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="다운로드">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                downloadFile(f.path);
                              }}
                            >
                              <DownloadIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </Paper>

      {/* 우측: 파일 뷰어 */}
      {viewingPath && (
        <Paper
          variant="outlined"
          sx={{ flex: 1, display: "flex", flexDirection: "column" }}
        >
          <Box
            sx={{
              p: 1,
              borderBottom: 1,
              borderColor: "divider",
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                flex: 1,
                fontFamily: "monospace",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {viewingPath}
            </Typography>
            {content?.truncated && (
              <Chip size="small" color="warning" variant="outlined" label="일부만 로드" />
            )}
            {!editing && content && isEditable(viewingPath, content.truncated) && (
              <Button size="small" startIcon={<EditIcon />} onClick={startEditing}>
                편집
              </Button>
            )}
            {editing && (
              <>
                <Button
                  size="small"
                  onClick={() => setEditing(false)}
                  disabled={saving}
                >
                  취소
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={
                    saving ? <CircularProgress size={12} color="inherit" /> : <SaveIcon />
                  }
                  onClick={saveFile}
                  disabled={saving}
                >
                  저장
                </Button>
              </>
            )}
            <Tooltip title="다운로드">
              <IconButton size="small" onClick={() => downloadFile(viewingPath)}>
                <DownloadIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <IconButton size="small" onClick={closeFile}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {toast && (
            <Alert severity={toast.severity} sx={{ m: 1 }}>
              {toast.message}
            </Alert>
          )}

          {fileLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress size={24} />
            </Box>
          ) : !content ? null : editing ? (
            <Box sx={{ flex: 1, p: 1 }}>
              <TextField
                multiline
                fullWidth
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                InputProps={{
                  sx: {
                    fontFamily: "monospace",
                    fontSize: 12,
                    alignItems: "flex-start",
                    height: "100%",
                  },
                }}
                sx={{
                  height: "100%",
                  "& .MuiInputBase-root": { height: "100%" },
                  "& .MuiInputBase-input": {
                    height: "100% !important",
                    overflow: "auto !important",
                  },
                }}
              />
            </Box>
          ) : (
            <Box sx={{ flex: 1, p: 1.5, overflow: "auto" }}>
              <Typography
                variant="caption"
                sx={{
                  fontFamily: "monospace",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                {content.content}
              </Typography>
            </Box>
          )}
        </Paper>
      )}
    </Box>
  );
}
